import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { SimpleTypewriterText } from './TypewriterText';

const ThankYouContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2rem;
  min-height: 300px;
  position: relative;
  overflow: hidden;
`;

const MainMessage = styled.div`
  font-family: 'Naveid Arabic DEMO', 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #000000;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 2;
`;

const SubMessage = styled(motion.div)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 400px;
  letter-spacing: 0.01em;
`;

const CheckmarkContainer = styled(motion.div)`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const Checkmark = styled(motion.svg)`
  width: 80px;
  height: 80px;
  stroke: #ffffff;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
`;



const AnimatedThankYou = ({ 
  mainMessage = "Thank You!",
  subMessage = "We'll get back to you soon.",
  showCheckmark = true
}) => {
  const [showSubMessage, setShowSubMessage] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Only set timer if sub message isn't showing yet
    if (!showSubMessage) {
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Show sub-message after main message animation
      timerRef.current = setTimeout(() => {
        setShowSubMessage(true);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [showSubMessage]); // Run when showSubMessage changes

  const renderMainMessage = useCallback(() => {
    return (
      <motion.div
        key="main-message-container"
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ 
          duration: 0.8, 
          ease: [0.4, 0, 0.2, 1],
          delay: 0.3
        }}
      >
        {mainMessage}
      </motion.div>
    );
  }, [mainMessage]);

  return (
    <ThankYouContainer
      key="thank-you-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >

      {showCheckmark && (
        <CheckmarkContainer
          key="checkmark-container"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.6, 
            ease: [0.4, 0, 0.2, 1],
            delay: 0.3
          }}
        >
          <Checkmark
            key="checkmark-svg"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ 
              duration: 0.6, 
              ease: "easeInOut",
              delay: 0.8
            }}
            viewBox="0 0 24 24"
          >
            <motion.path
              key="checkmark-path"
              d="M6 12l4 4 8-8"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ 
                duration: 0.5, 
                ease: "easeInOut",
                delay: 1.0
              }}
            />
          </Checkmark>
        </CheckmarkContainer>
      )}

      <MainMessage>
        {renderMainMessage()}
      </MainMessage>

      {showSubMessage && (
        <SubMessage key="sub-message">
          <SimpleTypewriterText 
            key="sub-message-text"
            speed={60} 
            delay={200}
          >
            {subMessage}
          </SimpleTypewriterText>
        </SubMessage>
      )}
    </ThankYouContainer>
  );
};

export default React.memo(AnimatedThankYou, (prevProps, nextProps) => {
  return (
    prevProps.mainMessage === nextProps.mainMessage &&
    prevProps.subMessage === nextProps.subMessage &&
    prevProps.showCheckmark === nextProps.showCheckmark
  );
});
